const POST_AUTH_INTENT_STORAGE_KEY = 'rs.postAuthIntent.v1';
const POST_AUTH_INTENT_MAX_AGE_MS = 1000 * 60 * 60 * 6;

interface StoredPostAuthIntent {
  path: string;
  createdAt: number;
  source?: string;
}

const canUseSessionStorage = (): boolean => {
  try {
    return typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';
  } catch (_error) {
    return false;
  }
};

const now = (): number => Date.now();

const isAuthRoute = (path: string): boolean => {
  return (
    path.startsWith('/login') ||
    path.startsWith('/register') ||
    path.startsWith('/verify-email') ||
    path.startsWith('/reset-password')
  );
};

const isCreateRequestIntent = (path: string): boolean => {
  return path === '/app/request/new' || path.startsWith('/app/request/new/');
};

const normalizeIntentPath = (path: unknown): string | null => {
  const rawPath = String(path || '').trim();
  if (!rawPath || !rawPath.startsWith('/')) {
    return null;
  }
  if (rawPath.startsWith('//') || rawPath.includes('://') || isAuthRoute(rawPath)) {
    return null;
  }
  if (!isCreateRequestIntent(rawPath)) {
    return null;
  }
  return rawPath;
};

const safelyDecodeUrlValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch (_error) {
    return value;
  }
};

export const resolvePostAuthIntentFromContinueUrl = (
  continueUrl: unknown,
  expectedOrigin?: string
): string | null => {
  const rawValue = String(continueUrl || '').trim();
  if (!rawValue) {
    return null;
  }

  const decodedValue = safelyDecodeUrlValue(rawValue).trim();
  if (!decodedValue || decodedValue.startsWith('//')) {
    return null;
  }

  const origin = String(
    expectedOrigin ||
      (typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '')
  ).trim();
  if (!origin) {
    return null;
  }

  try {
    const originUrl = new URL(origin);
    const candidateUrl = new URL(decodedValue, originUrl);
    if (
      candidateUrl.origin !== originUrl.origin ||
      candidateUrl.username ||
      candidateUrl.password
    ) {
      return null;
    }

    return normalizeIntentPath(
      `${candidateUrl.pathname}${candidateUrl.search}${candidateUrl.hash}`
    );
  } catch (_error) {
    return null;
  }
};

const readRawIntent = (): StoredPostAuthIntent | null => {
  if (!canUseSessionStorage()) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(POST_AUTH_INTENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as StoredPostAuthIntent;
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    return parsed;
  } catch (_error) {
    return null;
  }
};

export const clearPostAuthIntent = (): void => {
  if (!canUseSessionStorage()) {
    return;
  }
  try {
    window.sessionStorage.removeItem(POST_AUTH_INTENT_STORAGE_KEY);
  } catch (_error) {
    // no-op
  }
};

const isIntentExpired = (createdAt: number): boolean => {
  if (!Number.isFinite(createdAt) || createdAt <= 0) {
    return true;
  }
  return now() - createdAt > POST_AUTH_INTENT_MAX_AGE_MS;
};

export const savePostAuthIntent = (path: unknown, source = 'unknown'): string | null => {
  const normalizedPath = normalizeIntentPath(path);
  if (!normalizedPath || !canUseSessionStorage()) {
    return null;
  }

  const payload: StoredPostAuthIntent = {
    path: normalizedPath,
    createdAt: now(),
    source,
  };

  try {
    window.sessionStorage.setItem(POST_AUTH_INTENT_STORAGE_KEY, JSON.stringify(payload));
    return normalizedPath;
  } catch (_error) {
    return null;
  }
};

export const getPostAuthIntent = (): string | null => {
  const rawIntent = readRawIntent();
  if (!rawIntent) {
    return null;
  }

  if (isIntentExpired(rawIntent.createdAt)) {
    clearPostAuthIntent();
    return null;
  }

  const normalizedPath = normalizeIntentPath(rawIntent.path);
  if (!normalizedPath) {
    clearPostAuthIntent();
    return null;
  }

  return normalizedPath;
};

export const consumePostAuthIntent = (): string | null => {
  const intentPath = getPostAuthIntent();
  if (intentPath) {
    clearPostAuthIntent();
  }
  return intentPath;
};

export const encodePostAuthIntentForQuery = (path: unknown): string | null => {
  const normalizedPath = normalizeIntentPath(path);
  if (!normalizedPath) {
    return null;
  }
  return encodeURIComponent(normalizedPath);
};

export const decodePostAuthIntentFromQuery = (encodedPath: unknown): string | null => {
  const raw = String(encodedPath || '').trim();
  if (!raw) {
    return null;
  }

  try {
    const decoded = decodeURIComponent(raw);
    return normalizeIntentPath(decoded);
  } catch (_error) {
    return null;
  }
};

export const savePostAuthIntentFromQueryValue = (
  encodedPath: unknown,
  source = 'query-param'
): string | null => {
  const decodedPath = decodePostAuthIntentFromQuery(encodedPath);
  if (!decodedPath) {
    return null;
  }
  return savePostAuthIntent(decodedPath, source);
};
